import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  getDvSourceImageUrl,
  getLandingPageTitleColor,
  applyThemeButtonGradientStyles,
  applyThemeInputBoxColorStyle,
} from "../../functions/themeAssets";
import GameStageLayout from "./GameStageLayout";
import "./gameStageLayout.css";
import "../fonts/breuer-headline.css";
import "./unscrambleStage.css";
import Swal from "sweetalert2";
import UnscrambleRow from "./UnscrambleRow";
import {
  parseMinSubmitCount,
  parseShowClue,
  parseUnscrambleFromTheme,
} from "./unscrambleUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  isDemoUser,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { showEndGamePopup, navigateAfterCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";

const STAGE_NUM = 2;

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage2Points, setStage2Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [solvedRows, setSolvedRows] = useState(() => new Set());
  const [showCorrectPopup, setShowCorrectPopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const solvedRef = useRef(new Set());
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const stage2CompleteRef = useRef(false);
  const rowRefs = useRef([]);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => isDemoUser(storedUser), [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const entries = useMemo(
    () => parseUnscrambleFromTheme(themeData),
    [themeData]
  );

  const minSubmitCount = useMemo(
    () => parseMinSubmitCount(themeData, entries.length),
    [themeData, entries.length]
  );

  const showClue = useMemo(() => parseShowClue(themeData), [themeData]);

  const pointsPerWord = useMemo(() => {
    const p = Number(themeData?.points);
    return Number.isFinite(p) && p > 0 ? p : 10;
  }, [themeData?.points]);

  const title = useMemo(() => {
    const t =
      themeData?.unscramble_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Unscramble";
    return String(t).trim();
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const canSubmit = solvedRows.size >= minSubmitCount;

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    if (themeStatus !== "succeeded" || !themeData) return;
    applyThemeButtonGradientStyles(themeData);
    applyThemeInputBoxColorStyle(themeData);
  }, [themeStatus, themeData]);

  useEffect(() => {
    document.body.classList.add("unscramble-page");
    return () => {
      document.body.classList.remove("unscramble-page");
    };
  }, []);

  useEffect(() => {
    solvedRef.current = solvedRows;
  }, [solvedRows]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage2Points);
  }, [stage2Points]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded" || !entries.length) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage2 = Number(st.stage2) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage2);
          setStage2Points(dbStage2);
          setDisplayTotalScore(dbTotal);

          const solvedFromScore =
            pointsPerWord > 0
              ? Math.min(
                  entries.length,
                  Math.floor(dbStage2 / pointsPerWord)
                )
              : 0;
          if (solvedFromScore > 0) {
            const restored = new Set(
              Array.from({ length: solvedFromScore }, (_, i) => i)
            );
            setSolvedRows(restored);
            solvedRef.current = restored;
          }
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Game loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    entries.length,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    pointsPerWord,
    navigate,
  ]);

  const finishStage = useCallback(
    async () => {
      if (submitting || submittedRef.current) return;
      if (!canSubmit) {
        Swal.fire({
          icon: "info",
          title: "Not enough answers",
          text: `Solve at least ${minSubmitCount} word(s) before submitting.`,
        });
        return;
      }

      submittedRef.current = true;
      setSubmitting(true);

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stage2Score = stage2Points;
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage2Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage2Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }

        try {
          await fetch(`${backendBase}/welcomeStageComplete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId,
              time: timeStr,
              total_score: saveResult.total_score ?? finalTotal,
              points: saveResult.total_score ?? finalTotal,
            }),
          });
        } catch (err) {
          console.error("welcomeStageComplete:", err);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      const endGameImg = themeData?.end_game_img_popup;
      await showEndGamePopup({
        imageUrl: endGameImg ? getDvSourceImageUrl(endGameImg) : null,
        text: themeData?.end_game_text_popup,
      });

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigateAfterCelebration(navigate, "/thankyou");
    },
    [
      submitting,
      canSubmit,
      minSubmitCount,
      stage2Points,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      navigate,
      themeData,
    ]
  );

  const handleRowSolved = useCallback(
    (rowIndex) => {
      if (solvedRef.current.has(rowIndex)) return;
      const isFirstAnswerCompletion = solvedRef.current.size === 0;

      setSolvedRows((prev) => {
        const next = new Set(prev);
        next.add(rowIndex);
        return next;
      });

      setStage2Points((p) => p + pointsPerWord);

      const correctPopupImg = themeData?.puzzle_image;
      if (correctPopupImg || themeData?.Correct_ans_text_popup) {
        setShowCorrectPopup(true);
        setTimeout(() => setShowCorrectPopup(false), 1500);
      }

      const nextIndex = rowIndex + 1;
      if (nextIndex < entries.length) {
        setTimeout(() => rowRefs.current[nextIndex]?.focusFirst?.(), 0);
      }

      if (
        isDemoBypass &&
        isFirstAnswerCompletion &&
        !demoRedirectTriggeredRef.current
      ) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void Swal.fire({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
      }
    },
    [pointsPerWord, themeData, entries.length, isDemoBypass]
  );

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "unscramble_rules"),
      themeData,
    });
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="unscramble-stage__loading">Loading game…</div>;
  }

  if (!entries.length) {
    return (
      <div className="unscramble-stage__empty">
        No unscramble words configured. Add them in theme admin under Unscramble.
      </div>
    );
  }

  const correctPopupImg = themeData?.puzzle_image;

  return (
    <GameStageLayout
      title={title}
      themedTextStyle={themedTextStyle}
      elapsedClock={formatSecondsToClock(elapsedSec)}
      points={displayTotalScore}
      onHelp={showHelp}
      accent="purple"
      className="game-stage--unscramble"
      footer={
        <button
          type="button"
          className="game-stage__submit-btn"
          disabled={!canSubmit || submitting}
          onClick={() => finishStage()}
        >
          SUBMIT
        </button>
      }
    >
      <div className="unscramble-stage__content">
        <div className="unscramble-stage__list">
          {entries.map((entry, i) => (
            <UnscrambleRow
              key={`${entry.scrambled}-${i}`}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              index={i}
              scrambled={entry.scrambled}
              answer={entry.answer}
              clue={entry.clue}
              showClue={showClue}
              done={solvedRows.has(i)}
              onSolved={handleRowSolved}
              textStyle={themedTextStyle}
            />
          ))}
        </div>
      </div>

      {showCorrectPopup && (correctPopupImg || themeData?.Correct_ans_text_popup) && (
        <div className="game-stage__popup-overlay">
          <div
            className={`game-stage__popup${
              correctPopupImg ? " game-stage__popup--image" : " game-stage__popup--text"
            }`}
          >
            {correctPopupImg ? (
              <img src={getDvSourceImageUrl(correctPopupImg)} alt="Correct" />
            ) : (
              <>
                <div className="game-stage__popup-icon" aria-hidden="true">
                  ✅
                </div>
                <p className="game-stage__popup-text">
                  {themeData.Correct_ans_text_popup}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </GameStageLayout>
  );
}
